import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/app/PageBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Trash2, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { email: "", name: "", admin_role: "editor", project_ids: [], all_projects: true };

export default function AdminAccounts() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);      // admin doc or null
  const [form, setForm] = useState(EMPTY);
  const [purging, setPurging] = useState(false);

  const load = async () => {
    const [admins, projs] = await Promise.all([api.get("/admins"), api.get("/projects")]);
    setList(admins.data);
    setProjects(projs.data);
  };
  useEffect(() => { load(); }, []);

  const openInvite = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => {
    const ids = u.project_ids || [];
    setEditing(u);
    setForm({
      email: u.email,
      name: u.name || "",
      admin_role: u.admin_role || "editor",
      project_ids: ids,
      all_projects: ids.length === 0,
    });
    setOpen(true);
  };

  const toggleProject = (pid) => {
    setForm((f) => {
      const has = f.project_ids.includes(pid);
      return { ...f, project_ids: has ? f.project_ids.filter((x) => x !== pid) : [...f.project_ids, pid] };
    });
  };

  const submit = async () => {
    const payload = {
      name: form.name.trim(),
      admin_role: form.admin_role,
      project_ids: form.all_projects ? [] : form.project_ids,
    };
    if (!editing) payload.email = form.email.trim().toLowerCase();
    if (!editing && !payload.email) { toast.error("Email is required"); return; }
    if (!payload.name) { toast.error("Name is required"); return; }
    if (!form.all_projects && payload.project_ids.length === 0) {
      toast.error("Select at least one project or tick All projects"); return;
    }
    try {
      if (editing) {
        await api.patch(`/admins/${editing.user_id}`, payload);
        toast.success("Admin updated");
      } else {
        await api.post("/admins", payload);
        toast.success("Admin invited (Google sign-in enabled for this email)");
      }
      setOpen(false); setForm(EMPTY); setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const toggle = async (u, status) => {
    try { await api.patch(`/admins/${u.user_id}/status`, { status }); toast.success("Updated"); load(); }
    catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const purgeDemo = async () => {
    const first = window.prompt('This will DELETE all projects, customers, plots, invoices, payments, notes and attachments.\n\nType PURGE to confirm.');
    if (first !== 'PURGE') { toast.info('Cancelled'); return; }
    const alsoAdmins = window.confirm('Do you ALSO want to delete every other Admin account (excluding you)?\n\nOK = delete admins, Cancel = keep admins.');
    if (!window.confirm(`Final check: ${alsoAdmins ? 'DATA + ADMINS' : 'DATA only'} will be permanently removed. Proceed?`)) return;
    setPurging(true);
    try {
      const r = await api.post('/admin/purge-demo-data', { confirm: 'PURGE', wipe_audit_log: true, wipe_admins: alsoAdmins });
      const counts = r.data?.purged || {};
      const summary = Object.entries(counts).map(([k, v]) => `${k}: ${v}`).join(', ');
      toast.success(`Purged. ${summary}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Purge failed'); }
    finally { setPurging(false); }
  };

  const projectsLabel = (u) => {
    if (u.role === "super_admin") return "All projects";
    const ids = u.project_ids || [];
    if (ids.length === 0) return "All projects";
    const names = ids.map((id) => projects.find((p) => p.id === id)?.name).filter(Boolean);
    return names.length ? names.join(", ") : `${ids.length} project(s)`;
  };

  return (
    <div>
      <PageHeader
        title="Admin accounts"
        subtitle="Invite team members by their Google email, choose editor or viewer, and assign the projects they can access."
        actions={
          <div className="flex gap-2">
            {user?.role === "super_admin" && (
              <Button data-testid="purge-demo-btn" variant="outline" onClick={purgeDemo} disabled={purging}
                className="text-red-700 border-red-300 hover:bg-red-50">
                <Trash2 className="h-4 w-4 mr-2" />{purging ? "Purging…" : "Purge demo data"}
              </Button>
            )}
            <Button data-testid="invite-admin-btn" onClick={openInvite}><UserPlus className="h-4 w-4 mr-2" />Invite admin</Button>
          </div>
        }
      />

      <div className="border border-border rounded-md bg-card overflow-hidden">
        <table className="w-full text-sm m-cards">
          <thead className="bg-muted/60"><tr className="text-left">
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Name</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Email</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Role</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Access</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Projects</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Status</th>
            <th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.user_id} data-testid={`admin-row-${u.user_id}`} className="border-t border-border">
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-4 py-3 capitalize">{u.role.replace("_", " ")}</td>
                <td className="px-4 py-3">
                  {u.role === "super_admin"
                    ? <span className="text-xs px-2 py-1 rounded bg-primary/10 text-primary">Full</span>
                    : <span className="text-xs capitalize px-2 py-1 rounded bg-muted">{u.admin_role || "editor"}</span>
                  }
                </td>
                <td className="px-4 py-3 text-muted-foreground text-xs max-w-xs truncate" title={projectsLabel(u)}>{projectsLabel(u)}</td>
                <td className="px-4 py-3"><StatusBadge status={u.status} /></td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    {u.role === "admin" && (
                      <Button data-testid={`edit-${u.user_id}`} size="sm" variant="ghost" onClick={() => openEdit(u)} title="Edit access">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {u.role === "admin" && (
                      u.status === "active"
                        ? <Button data-testid={`deactivate-${u.user_id}`} size="sm" variant="outline" onClick={() => toggle(u, "inactive")}>Deactivate</Button>
                        : <Button data-testid={`activate-${u.user_id}`} size="sm" onClick={() => toggle(u, "active")}>Activate</Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No admins yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? `Edit access · ${editing.name}` : "Invite admin"}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div><Label>Name</Label><Input data-testid="admin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div>
              <Label>Google email</Label>
              <Input data-testid="admin-email" value={form.email}
                disabled={!!editing}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.admin_role} onValueChange={(v) => setForm({ ...form, admin_role: v })}>
                <SelectTrigger data-testid="admin-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="editor">Editor — can create, edit and delete records</SelectItem>
                  <SelectItem value="viewer">Viewer — read-only access</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Projects</Label>
              <div className="border border-border rounded-md p-3 mt-1 space-y-2 max-h-56 overflow-auto">
                <label className="flex items-center gap-2 pb-2 border-b border-border cursor-pointer">
                  <Checkbox data-testid="admin-all-projects"
                    checked={form.all_projects}
                    onCheckedChange={(v) => setForm({ ...form, all_projects: !!v, project_ids: v ? [] : form.project_ids })} />
                  <span className="text-sm font-medium">All projects (current & future)</span>
                </label>
                {!form.all_projects && (
                  <div className="space-y-1.5 pt-1">
                    {projects.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 cursor-pointer text-sm">
                        <Checkbox data-testid={`admin-proj-${p.id}`}
                          checked={form.project_ids.includes(p.id)}
                          onCheckedChange={() => toggleProject(p.id)} />
                        <span>{p.name}</span>
                      </label>
                    ))}
                    {projects.length === 0 && <div className="text-xs text-muted-foreground">No projects available. Create a project first.</div>}
                  </div>
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-1.5">Viewers see only the projects assigned here. Editors can create/edit records only within their assigned projects.</div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="invite-submit" onClick={submit}>{editing ? "Save changes" : "Invite"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
